import { useEffect } from "react";

import { useForm } from "@ui/components/Form";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode } from "@ui/utils/backendError";

import { translateBackendErrorCode } from "@jti/fe-common";
import {
  type ReferralCodeSubmissionSource,
  useReferralCustomer,
  useReferralStatus,
  useReferralVerifyCode,
  useUpdateReferralCode,
} from "@jti/rest-api";
import type { IUser } from "@jti/rest-api-types";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { LoyaltyReferralCodeForm, LoyaltyReferralCodeProps } from "../types";

type UseLoyaltyReferralCodeArgs = {
  onCodeApplied?: LoyaltyReferralCodeProps["onCodeApplied"];
  verifyErrorMessage: string;
  whenHasParentCode?: () => void;
  source?: ReferralCodeSubmissionSource;
};

export const useLoyaltyReferralCode = ({
  onCodeApplied,
  verifyErrorMessage,
  whenHasParentCode,
  source,
}: UseLoyaltyReferralCodeArgs) => {
  const { dictionary } = useTranslations();
  const { user } = useUser();
  const userData = {
    uuid: user?.uuid ?? "",
    ciam_token: user?.ciam_token ?? "",
  };
  const referrals = {
    can_enter_a_code: user?.referrals?.can_enter_a_code ?? false,
    can_receive_discount: user?.referrals?.can_receive_discount ?? false,
  };

  const form = useForm<LoyaltyReferralCodeForm>({
    defaultValues: {
      code: "",
    },
  });

  const status = useReferralStatus();
  const customer = useReferralCustomer(userData);
  const verifyCode = useReferralVerifyCode(user as IUser);
  const updateReferralCode = useUpdateReferralCode();

  const parentCode = customer.data?.parent_code || user?.parent_code;

  const isSuccess = (updateReferralCode.isSuccess && Boolean(updateReferralCode.data)) || Boolean(parentCode);

  const showCard =
    status?.data &&
    !status.isLoading &&
    customer.data &&
    !customer.isLoading &&
    (referrals.can_enter_a_code || referrals.can_receive_discount) &&
    !customer.data?.has_first_purchase &&
    Boolean(user?.uuid);

  const {
    formState: {
      errors: { code: codeFormError },
    },
  } = form;

  const codeCustomErrorMessage = codeFormError?.type === "custom" ? codeFormError.message : null;

  const onSubmit = async () => {
    const referralCode = form.getValues().code.trim();
    try {
      const isValid = await verifyCode.mutateAsync({ referralCode });
      if (isValid) {
        const isApplied = await updateReferralCode.mutateAsync({
          referralCode,
          uuid: user?.uuid ?? "",
          source,
        });
        if (isApplied) {
          await dispatchTrackingEvent(TrackingEvents.TrackingReferralCodeInserted);
          onCodeApplied?.();
        }
        if (!isApplied || updateReferralCode.isError) {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      const errorMessage = translateBackendErrorCode(getBackendErrorCode(error), verifyErrorMessage, dictionary);
      form.setError("code", {
        type: "custom",
        message: errorMessage,
      });
    }
  };

  useEffect(() => {
    if (parentCode && !form.getValues("code")) {
      form.setValue("code", parentCode);
      whenHasParentCode?.();
    }
  }, [form, parentCode, whenHasParentCode]);

  return {
    user,
    form,
    status,
    customer,
    verifyCode,
    updateReferralCode,
    codeFormError,
    onSubmit,
    isSuccess,
    codeCustomErrorMessage,
    hideCard: !showCard,
    showCard,
  };
};
